import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

interface BlogLayoutProps {
  title: string;
  description: string;
  children: React.ReactNode;
  publishDate?: string;
  readTime?: string;
}

export const BlogLayout = ({ title, description, children, publishDate, readTime }: BlogLayoutProps) => {
  return (
    <>
      <Helmet>
        <title>{title} | AI StoryCraft Studio</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={`${title} | AI StoryCraft Studio`} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="article" />
        <meta name="twitter:title" content={`${title} | AI StoryCraft Studio`} />
        <meta name="twitter:description" content={description} />
      </Helmet>
      <Navbar />
      <main className="pt-24 pb-16 min-h-screen" style={{ background: "#FFFFFF" }}>
        <article className="max-w-[720px] mx-auto px-4 md:px-6">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm mb-8 hover:text-[#7C3AED] transition-colors" style={{ color: "#6B7280" }}>
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>
          <h1 className="font-display text-3xl md:text-4xl lg:text-[42px] font-bold leading-tight mb-4" style={{ color: "#111827" }}>
            {title}
          </h1>
          {(publishDate || readTime) && (
            <div className="flex items-center gap-3 text-sm mb-10" style={{ color: "#9CA3AF" }}>
              {publishDate && <span>{publishDate}</span>}
              {publishDate && readTime && <span>·</span>}
              {readTime && <span>{readTime}</span>}
            </div>
          )}
          <div className="prose prose-lg max-w-none [&_h2]:font-display [&_h2]:text-2xl [&_h2]:md:text-3xl [&_h2]:font-bold [&_h2]:mt-12 [&_h2]:mb-4 [&_h2]:text-[#111827] [&_h3]:font-display [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:text-[#111827] [&_p]:text-[#374151] [&_p]:leading-relaxed [&_p]:mb-5 [&_ul]:text-[#374151] [&_ul]:mb-5 [&_ol]:text-[#374151] [&_ol]:mb-5 [&_li]:mb-2 [&_strong]:text-[#111827] [&_a]:text-[#7C3AED] [&_a]:underline [&_a]:hover:text-[#6D28D9]">
            {children}
          </div>

          {/* CTA */}
          <div className="mt-16 p-8 md:p-10 rounded-2xl text-center" style={{ background: "#F9FAFB", border: "1px solid #E5E7EB" }}>
            <h3 className="font-display text-2xl font-bold mb-3" style={{ color: "#111827" }}>
              Ready to Start Monetising Your Content?
            </h3>
            <p className="mb-6 text-base" style={{ color: "#6B7280" }}>
              Join thousands of creators using AI StoryCraft Studio to turn their audience into income.
            </p>
            <Button size="lg" asChild className="bg-[#7C3AED] text-white font-bold hover:bg-[#6D28D9] border-none rounded-xl px-8">
              <Link to="/#pricing">Start Your Free Trial</Link>
            </Button>
          </div>
        </article>
      </main>
      <FooterSection />
    </>
  );
};
